// Central content store for Danny Clans Media (i18n-driven).
// Each "content" item holds a `tk` (translation key) that resolves under `content.*` in locale files.
import {
  Wallet, CreditCard, LayoutDashboard, ShieldCheck, Globe2, BarChart3,
  Layers, Gem, Briefcase, Gamepad2, ShoppingBag, Smartphone, Lock,
  HandCoins, FileText, CheckCircle2, Users, Receipt, Building2, Sparkles,
  Headphones, Rocket, Coins, Workflow,
} from "lucide-react";

export const BRAND = {
  name: "Danny Clans Media",
  tagline: "Scale the Unscalable",
  marketingLine: "We Scale What Others Can't.",
  jurisdiction: "Hong Kong SAR Incorporated",
  complianceLine: "Hong Kong SAR Incorporated • Regulatory Compliant",
  responsePromise: "2–3 hour response",
  logoUrl: "/logo.webp",
  logoUrlFallback: "/logo.png",
};

export const CONTACT = {
  emailHelp: "help@dannyclans.com",
  emailCollab: "collab@dannyclans.com",
  whatsapp: "+44 7520 656967",
  whatsappE164: "447520656967",
  addressLine1: "S022, 2/F, The Capital",
  addressLine2: "61–65 Chatham Road South",
  addressLine3: "Tsim Sha Tsui, Hong Kong SAR",
  hours: "Weekdays · 09:00–19:00 HKT",
};

export const NAV_LINKS = [
  { key: "nav.dashboard", to: "/dashboard" },
  { key: "nav.industries", to: "/industries" },
  { key: "nav.engagement", to: "/engagement" },
  { key: "nav.pricing", to: "/pricing" },
  { key: "nav.insights", to: "/blog" },
  { key: "nav.about", to: "/about" },
  { key: "nav.contact", to: "/contact" },
];

export const DASHBOARD_SCREENSHOTS = [
  { key: "display", tk: "screenshots.display", src: "/screenshots/display.webp", fallback: "/screenshots/display.jpeg", width: 1600, height: 900, cropLeft: 22 },
  { key: "wallet",  tk: "screenshots.wallet",  src: "/screenshots/wallet.webp",  fallback: "/screenshots/wallet.png",   width: 1080, height: 1080 },
  { key: "deposit", tk: "screenshots.deposit", src: "/screenshots/deposit.webp", fallback: "/screenshots/deposit.jpeg", width: 1536, height: 1024 },
  { key: "console", tk: "screenshots.console", src: "/screenshots/console.webp", fallback: "/screenshots/console.png",  width: 1080, height: 1080 },
];

const si = (slug, color = "64748B") => `https://cdn.simpleicons.org/${slug}/${color}`;
export const PARTNER_LOGOS = [
  { name: "Meta", src: si("meta") }, { name: "Google", src: si("google") }, { name: "TikTok", src: si("tiktok") },
  { name: "X", src: si("x") }, { name: "Snapchat", src: si("snapchat") }, { name: "LinkedIn", src: si("linkedin") },
  { name: "YouTube", src: si("youtube") }, { name: "Bing", src: si("microsoftbing") }, { name: "Reddit", src: si("reddit") },
  { name: "Pinterest", src: si("pinterest") }, { name: "Spotify", src: si("spotify") }, { name: "ByteDance", src: si("bytedance") },
  { name: "Kuaishou", src: si("kuaishou") }, { name: "LINE", src: si("line") }, { name: "Naver", src: si("naver") },
];

// Channel names + categories are platform/brand names — kept untranslated.
export const SUPPORTED_CHANNELS = [
  { name: "Meta",       category: "Social",     logo: `https://cdn.simpleicons.org/meta/0866FF` },
  { name: "Google Ads", category: "Search",     logo: `https://cdn.simpleicons.org/google/4285F4` },
  { name: "TikTok",     category: "Social",     logo: `https://cdn.simpleicons.org/tiktok/000000` },
  { name: "YouTube",    category: "Video",      logo: `https://cdn.simpleicons.org/youtube/FF0000` },
  { name: "Bing",       category: "Search",     logo: `https://cdn.simpleicons.org/microsoftbing/008373` },
  { name: "LinkedIn",   category: "B2B",        logo: `https://cdn.simpleicons.org/linkedin/0A66C2` },
  { name: "Snapchat",   category: "Social",     logo: `https://cdn.simpleicons.org/snapchat/FFFC00` },
  { name: "X (Twitter)",category: "Social",     logo: `https://cdn.simpleicons.org/x/000000` },
  { name: "Reddit",     category: "Community",  logo: `https://cdn.simpleicons.org/reddit/FF4500` },
  { name: "Pinterest",  category: "Discovery",  logo: `https://cdn.simpleicons.org/pinterest/BD081C` },
  { name: "ByteDance",  category: "Native",     logo: `https://cdn.simpleicons.org/bytedance/000000` },
  { name: "Kuaishou",   category: "Social",     logo: `https://cdn.simpleicons.org/kuaishou/FF4906` },
  { name: "LINE",       category: "Messaging",  logo: `https://cdn.simpleicons.org/line/00C300` },
  { name: "Naver",      category: "Search KR",  logo: `https://cdn.simpleicons.org/naver/03C75A` },
  { name: "Outbrain",   category: "Native",     logo: null },
  { name: "Taboola",    category: "Native",     logo: null },
];

export const PAYMENT_METHODS = [
  { tk: "paymentMethods.bankUsd",  icon: Building2 },
  { tk: "paymentMethods.visa",     icon: CreditCard },
  { tk: "paymentMethods.upi",      icon: Wallet },
  { tk: "paymentMethods.inrWallet",icon: Wallet },
  { tk: "paymentMethods.usdt",     icon: Receipt },
];

export const DASHBOARD_FEATURES = [
  { key: "master",    tk: "dashboardFeatures.master",    icon: LayoutDashboard },
  { key: "oneWallet", tk: "dashboardFeatures.oneWallet", icon: Wallet },
  { key: "campaign",  tk: "dashboardFeatures.campaign",  icon: Sparkles },
  { key: "reports",   tk: "dashboardFeatures.reports",   icon: BarChart3 },
  { key: "support",   tk: "dashboardFeatures.support",   icon: Headphones },
];

export const CORE_ADVANTAGES = [
  { key: "agencyAccess",         tk: "advantages.agencyAccess",         icon: Gem },
  { key: "oneWalletMany",        tk: "advantages.oneWalletMany",        icon: Wallet },
  { key: "complianceBuiltIn",    tk: "advantages.complianceBuiltIn",    icon: ShieldCheck },
  { key: "b2bRates",             tk: "advantages.b2bRates",             icon: HandCoins },
  { key: "transparentReporting", tk: "advantages.transparentReporting", icon: BarChart3 },
  { key: "hkOperated",           tk: "advantages.hkOperated",           icon: Globe2 },
];

// Region content (channels stay as raw strings since they're platform names).
export const REGIONS = [
  { key: "sea",   tk: "regions.sea",   channels: ["Meta", "TikTok", "Google", "LINE", "Bing"] },
  { key: "emea",  tk: "regions.emea",  channels: ["Meta", "Google", "YouTube", "LinkedIn", "Reddit", "Bing"] },
  { key: "jpkr",  tk: "regions.jpkr",  channels: ["LINE", "Naver", "Yahoo JP", "Google", "Meta", "TikTok"] },
  { key: "latam", tk: "regions.latam", channels: ["Meta", "TikTok", "Google", "Kuaishou", "YouTube"] },
  { key: "mena",  tk: "regions.mena",  channels: ["Meta", "TikTok", "Google", "Snapchat", "X"] },
];

export const INDUSTRIES = [
  { key: "brand",  tk: "industries.brand",  icon: Briefcase },
  { key: "gaming", tk: "industries.gaming", icon: Gamepad2 },
  { key: "apps",   tk: "industries.apps",   icon: Smartphone },
  { key: "ecom",   tk: "industries.ecom",   icon: ShoppingBag },
];

export const WHY_DC_REASONS = [
  { key: "noGuarantees",    tk: "whyReasons.noGuarantees",    icon: ShieldCheck },
  { key: "complianceFirst", tk: "whyReasons.complianceFirst", icon: FileText },
  { key: "expertToExpert",  tk: "whyReasons.expertToExpert",  icon: Users },
  { key: "transparentCost", tk: "whyReasons.transparentCost", icon: HandCoins },
  { key: "hkJurisdiction",  tk: "whyReasons.hkJurisdiction",  icon: Globe2 },
  { key: "whiteLabel",      tk: "whyReasons.whiteLabel",      icon: Lock },
];

export const INSTITUTIONAL_PROOFS = [
  { key: "hkIncorp",          tk: "institutionalProofs.hkIncorp",          icon: ShieldCheck },
  { key: "multiPlatform",     tk: "institutionalProofs.multiPlatform",     icon: CheckCircle2 },
  { key: "complianceOps",     tk: "institutionalProofs.complianceOps",     icon: CheckCircle2 },
  { key: "transparentLimits", tk: "institutionalProofs.transparentLimits", icon: FileText },
];

export const SERVICES_OPTIONS = [
  "services.launch", "services.growth", "services.scale", "services.dominance", "services.enterprise",
  "services.provisioning", "services.walletHelp", "services.whiteLabel", "services.complianceConsult",
];

export const PRICING_PLANS = [
  { key: "launch",     tk: "pricing.launch",     price: "$0",       cadence: "/ month", highlighted: false },
  { key: "growth",     tk: "pricing.growth",     price: "$300",     cadence: "/ month", highlighted: false },
  { key: "scale",      tk: "pricing.scale",      price: "$500",     cadence: "/ month", highlighted: true },
  { key: "dominance",  tk: "pricing.dominance",  price: "$1,000",   cadence: "/ month", highlighted: false },
  { key: "mediabuyer", tk: "pricing.mediabuyer", price: "$15,000",  cadence: "/ month", highlighted: false, enterprise: true },
];

export const ONBOARDING_STEPS = [
  { step: "01", key: "signup", tk: "onboardingSteps.signup", icon: ShieldCheck },
  { step: "02", key: "plan",   tk: "onboardingSteps.plan",   icon: Layers },
  { step: "03", key: "apply",  tk: "onboardingSteps.apply",  icon: LayoutDashboard },
  { step: "04", key: "topup",  tk: "onboardingSteps.topup",  icon: Wallet },
  { step: "05", key: "launch", tk: "onboardingSteps.launch", icon: Rocket },
];

export const ENGAGEMENT_PRINCIPLES = [
  { key: "prepaid",    tk: "engagementPrinciples.prepaid",    icon: Coins },
  { key: "scope",      tk: "engagementPrinciples.scope",      icon: FileText },
  { key: "compliance", tk: "engagementPrinciples.compliance", icon: ShieldCheck },
  { key: "auditReady", tk: "engagementPrinciples.auditReady", icon: Workflow },
];

export const ABOUT_PRINCIPLES = [
  { key: "compliance",     tk: "aboutPrinciples.compliance" },
  { key: "infrastructure", tk: "aboutPrinciples.infrastructure" },
  { key: "documented",     tk: "aboutPrinciples.documented" },
  { key: "expert",         tk: "aboutPrinciples.expert" },
];
